import type { UserResponse } from '@shared/schemas';
import { all, call, getContext, put, takeLatest } from 'redux-saga/effects';

import type { UsersGateway } from '../infrastructure/users.gateway';
import { archiveRequested, deleteRequested, usersRequested } from './users.actions';
import { usersActions } from './users.slice';

const LOAD_ERROR = 'Failed to load users.';
const DELETE_ERROR = 'Failed to delete user.';
const DEACTIVATE_ERROR = 'Failed to deactivate this user.';
const REACTIVATE_ERROR = 'Failed to reactivate this user.';

/** Best-effort human message for the failure banner / snackbar (no domain error union). */
function toMessage(e: unknown, fallback: string): string {
  if (e instanceof TypeError) {
    return 'Network error; please check your connection and retry.';
  }
  return e instanceof Error && e.message ? e.message : fallback;
}

/** WORKER: fetch every user. All I/O via the injected gateway. */
function* fetchWorker() {
  const gateway: UsersGateway = yield getContext('usersGateway');
  yield put(usersActions.loadStarted());
  try {
    const rows = (yield call(gateway.fetchUsers)) as UserResponse[];
    yield put(usersActions.loadSucceeded(rows));
  } catch (e) {
    yield put(usersActions.loadFailed(toMessage(e, LOAD_ERROR)));
  }
}

/** WORKER: delete a row, dropping it from state on success. */
function* deleteWorker(action: ReturnType<typeof deleteRequested>) {
  const id = action.payload;
  const gateway: UsersGateway = yield getContext('usersGateway');
  yield put(usersActions.deleteStarted());
  try {
    yield call(gateway.deleteUser, id);
    yield put(usersActions.deleteSucceeded(id));
  } catch (e) {
    yield put(usersActions.deleteFailed(toMessage(e, DELETE_ERROR)));
  }
}

/** WORKER: deactivate or reactivate a row, writing the saved `archived_at` back. */
function* archiveWorker(action: ReturnType<typeof archiveRequested>) {
  const { id, archived } = action.payload;
  const gateway: UsersGateway = yield getContext('usersGateway');
  yield put(usersActions.archiveStarted());
  try {
    const { archivedAt } = (yield call(gateway.setArchived, { id, archived })) as {
      archivedAt: string | null;
    };
    yield put(usersActions.archiveSucceeded({ id, archivedAt }));
  } catch (e) {
    yield put(
      usersActions.archiveFailed(toMessage(e, archived ? DEACTIVATE_ERROR : REACTIVATE_ERROR)),
    );
  }
}

/** WATCHERS. */
export function* usersSaga() {
  yield all([
    takeLatest(usersRequested.type, fetchWorker),
    takeLatest(deleteRequested.type, deleteWorker),
    takeLatest(archiveRequested.type, archiveWorker),
  ]);
}
